import { useCallback, useEffect } from "react";
import { getMenuAuth } from "src/apis/authApi";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { downloadExcel } from "src/utils/blob/blobUtil";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { exportProductResultExcel, getProductResultAllCount, getProductResultList } from "./Api_KSK2070_R01";

import useMenuStore from "src/store/useMenuStore";
import { fetchFilterConfig } from "src/utils/grid/GridHandlerContent";
import { KSK2070_R01Dto } from "./Dto_KSK2070_R01";
import { defaultKSK2070_R01Filter, ExtendedKSK2070_R01Dto, KSK2070_R01Filter, useKSK2070_R01Store } from "./Store_KSK2070_R01";


interface ProductResultContentProps {
    setMessage: (message: string) => void;
}

export const Content_KSK2070_R01 = (props: ProductResultContentProps) => {
    const {
		setMessage,
    } = props;

    // 전역 store에서 menuCd 가져오기
	const { 
		KSK2070_R01Filter, setKSK2070_R01Filter,
		KSK2070_R01PastFilter, setKSK2070_R01PastFilter,
		KSK2070_R01List, setKSK2070_R01List, 
		setAllCount,
	} = useKSK2070_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
    const { menuList } = useMenuStore();
    const menuCdStore = menuList[0]?.menuCd;
    const setAuth = useActionbarAuthStore((state) => state.setAuth);

	useFetchActionbarAuth("KSK2070_R01");

    useEffect(() => {
        getMenuAuth("KSK2070_R01").then((res) => {
            setAuth(res.data);
        });
    }, []);

    useEffect(() => {
        if (!menuCdStore) {
            console.log("[ProductResultContent] menuCd가 없습니다. 메뉴를 선택하세요.");
        } else {
            console.log("[ProductResultContent] menuCd:" + menuCdStore);
        }
    }, [menuCdStore]);

	const getParams = (filter: Partial<KSK2070_R01Filter>) => {
		return {
			compCd: filter.compCd,
			productDateRange: filter.productDateRange,
			orderType: filter.orderType,
			workplaceCd: filter.workplaceCd,
			itemCd: filter.itemCd,
			brandCd: filter.brandCd
		};
	}

	const renewalAllCount = useCallback(async () => {
		const response = await getProductResultAllCount(getParams(KSK2070_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [KSK2070_R01Filter, setAllCount]);

    const fetchData = useCallback(async () => {
        setLoadingSpinner(true);
        try {

            const response = await getProductResultList(
                '',
                getParams(KSK2070_R01Filter),
                KSK2070_R01List.length ?? 0
            );

            // response.data.data를 PRD3040_R01Dto[]로 타입 캐스팅
            const rawData = response.data.data as KSK2070_R01Dto[];
            
            // isSubtotal 속성을 먼저 추가하여 ExtendedPRD3040_R01Dto[]로 변환
            const extendedRawData: ExtendedKSK2070_R01Dto[] = rawData.map(item => ({
                ...item,
                isSubtotal: false
            }));

			const newList = [...extendedRawData ] as ExtendedKSK2070_R01Dto[];
			setKSK2070_R01List(newList);

            const processAllCount = await renewalAllCount();

            if (newList && newList.length > 0) {
                setKSK2070_R01PastFilter(KSK2070_R01Filter);
                setKSK2070_R01List(newList);
                ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
            } else {
                showWarning('데이터가 없습니다.');
                setKSK2070_R01List([]);
            }
        } catch (error) {
            console.error('데이터 조회 실패:', error);
            showWarning('데이터 조회에 실패했습니다.');
            setKSK2070_R01List([]);
        } finally {
            setLoadingSpinner(false);
        }
    }, [KSK2070_R01Filter, setKSK2070_R01List, setKSK2070_R01PastFilter]);
    
    const handleExcelClick = async () => {
        const {isDefaultFilter, isDefaultPastFilter, listLength} = fetchFilterConfig(
            KSK2070_R01Filter,
            KSK2070_R01PastFilter,
            defaultKSK2070_R01Filter,
            KSK2070_R01List.length
        );

        const response = await exportProductResultExcel(
            isDefaultFilter? '' : 'search',
            getParams(KSK2070_R01Filter),
            listLength
        );
        const binaryString = atob(response.data.data);
        const byteArray = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
        }
        // Blob 생성
        const blob = new Blob([byteArray]);
        downloadExcel(blob, '생산실적 조회-excel.xlsx');
    };


    return {
        fetchData,
        handleExcelClick,
    };
};
